import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled, { createGlobalStyle } from 'styled-components'
import { motion } from 'framer-motion'

import { colors, fontSizes, zIndex } from '@/styles'
import useRole from '@/hooks/use-role'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { ArrowRightIconBlue, CloseButtonGrey96 } from '@/components/icons'

import LeftSideOfHeader from '../base/left-side-of-header'
import {
  renderAccountLabelMobile,
  renderChatOptionMobile,
  renderCreditUsageOptionMobile,
  renderFavouritesOptionMobile,
  renderLandlordsOptionMobile,
  renderLandlordsSidebarHeader,
  renderListingsOptionMobile,
  renderLogoutOptionMobile,
  renderManageLabelMobile,
  renderManageOptionMobile,
  renderManageSidebarHeader,
  renderPricingOptionLoggedMobile,
  renderPricingOptionMobile,
  renderProfileOptionMobile,
  renderRentalsOptionMobile,
  renderRentersOptionMobile,
  renderRentersSidebarHeader,
  renderResourcesOptionMobile,
  renderResourcesSidebarHeader,
  renderSavedAlertsOptionMobile,
  renderSettingsOptionMobile,
  renderTeamOptionMobile,
  renderTenantsOptionMobile,
  renderTrustScoreOptionMobile
} from './rendered-components'
import SidebarBottomContainerMobile from './sidebar-bottom-container-mobile'
import SidebarLinkMobile from './sidebar-link-mobile'
import {
  CloseButton,
  SidebarHeader,
  SidebarLabelMobile
} from './simple-components'

const SidebarContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 100%;
  height: 100%;
  width: 100%;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 0 20px;
  overflow-y: auto;
  position: fixed;
  z-index: ${zIndex.dropdownMenu};
`

const MenuOptions = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 10px 0 0;
  padding: 0 16px;
`

const Divider = styled.div`
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  width: 100%;
  margin: 4px 0;
`

const SeeAllFeaturesLink = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  color: ${colors.link};
  gap: 10px;
  text-decoration: none;
  background: none;
  font-weight: 600;
  font-size: ${fontSizes.regular};
  padding: 12px 4px;
`

const NoScroll = createGlobalStyle`
  html {
    overflow: hidden;
    position: fixed;
    max-height: 100%;
  }
`

const Sidebar = ({
  changeIsOpenToFalse,
  changeSetStateOfSidebar,
  stateOfSidebar,
  isOpen,
  hasSession,
  hasEssentialsPlan,
  signupClick,
  loginClick,
  loginClickRedirectChat,
  loginClickRedirectListings,
  logoutClick,
  closeSidebar
}) => {
  const { t } = useTranslation()
  const role = useRole()
  const isTenant = role === 'tenant'
  const [menuType, setMenuType] = useState(null)
  const sidebarRef = useRef(null)
  const isLoggedIn = !!role

  const slideOut = useCallback(() => {
    sidebarRef.current.style.transition = '450ms'
    sidebarRef.current.style.left = '100%'

    setTimeout(() => {
      changeSetStateOfSidebar('unmounted')
      changeIsOpenToFalse()
    }, 450)
  }, [changeSetStateOfSidebar, changeIsOpenToFalse])

  useEffect(() => {
    const slideIn = () => {
      changeSetStateOfSidebar('mounted')
    }

    slideIn()
    // Adding changeSetStateOfSidebar into dependency array causes animation jank
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (stateOfSidebar === 'unmounting') {
      slideOut()
    }
  }, [stateOfSidebar, slideOut])

  const clickedManageOption = () => {
    setMenuType('Manage')
  }

  const clickedLandlordsOption = () => {
    setMenuType('Landlords')
  }

  const clickedRentersOption = () => {
    setMenuType('Renters')
  }

  const clickedResourcesOption = () => {
    setMenuType('Resources')
  }

  const clickedSavedAlertsOption = () => {
    showLoginModal({
      mode: 'registerSavedAlertsNav',
      eventId: 'web-signup-submit-savedalertbutton',
      t
    })
  }

  const clickedBack = () => {
    setMenuType(null)
  }

  const renderPublicMenuMobile = () => {
    return (
      <>
        <MenuOptions>
          {renderManageOptionMobile(t, clickedManageOption)}
          {renderLandlordsOptionMobile(t, clickedLandlordsOption)}
          {renderRentersOptionMobile(t, clickedRentersOption)}
          {renderResourcesOptionMobile(t, clickedResourcesOption)}
          {renderPricingOptionMobile(t)}
        </MenuOptions>
        <SidebarBottomContainerMobile
          loginClick={loginClick}
          signupClick={signupClick}
        />
      </>
    )
  }

  const renderTenantMenuMobile = () => {
    return (
      <MenuOptions>
        {renderManageLabelMobile(t, 'logged-')}
        {renderProfileOptionMobile(t)}
        {renderChatOptionMobile(t, 'logged-')}
        {renderRentalsOptionMobile(t, 'logged-')}
        {renderFavouritesOptionMobile(t, 'logged-')}
        {renderSavedAlertsOptionMobile(t, clickedSavedAlertsOption, isLoggedIn)}
        {renderSettingsOptionMobile(t)}
        <Divider />
        {renderLandlordsOptionMobile(t, clickedLandlordsOption)}
        {renderRentersOptionMobile(t, clickedRentersOption)}
        {renderResourcesOptionMobile(t, clickedResourcesOption)}
        <Divider />
        {renderLogoutOptionMobile(t, logoutClick)}
      </MenuOptions>
    )
  }

  const renderLandlordMenuMobile = () => {
    return (
      <MenuOptions>
        {renderManageLabelMobile(t, 'logged-')}
        {renderChatOptionMobile(t, 'logged-')}
        {renderListingsOptionMobile(t, 'logged-')}
        {renderTenantsOptionMobile(t, 'logged-')}
        {renderTrustScoreOptionMobile(t, 'logged-')}
        {renderCreditUsageOptionMobile(t, 'logged-')}
        <Divider />
        {renderAccountLabelMobile(t, 'logged-')}
        {renderProfileOptionMobile(t)}
        {renderTeamOptionMobile(t, hasEssentialsPlan)}
        {renderSavedAlertsOptionMobile(t, clickedSavedAlertsOption, isLoggedIn)}
        {renderPricingOptionLoggedMobile(t)}
        {renderSettingsOptionMobile(t)}
        <Divider />
        {renderLandlordsOptionMobile(t, clickedLandlordsOption)}
        {renderRentersOptionMobile(t, clickedRentersOption)}
        {renderResourcesOptionMobile(t, clickedResourcesOption)}
        <Divider />
        {renderLogoutOptionMobile(t, logoutClick)}
      </MenuOptions>
    )
  }

  const renderManageSubMenuMobile = t => {
    return (
      <MenuOptions>
        {renderChatOptionMobile(t, '', loginClickRedirectChat)}
        {renderListingsOptionMobile(t, '', loginClickRedirectListings)}
        {renderTrustScoreOptionMobile(t)}
        {renderCreditUsageOptionMobile(t)}
        {renderFavouritesOptionMobile(t)}
        {renderSavedAlertsOptionMobile(t, clickedSavedAlertsOption)}
      </MenuOptions>
    )
  }

  const renderLandlordsSubMenuMobile = t => {
    return (
      <MenuOptions>
        <SidebarLabelMobile>{t('Features', 'Features')}</SidebarLabelMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/screening/"
          id="header-landlords-screening"
        >
          {t('Screening', 'Screening')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/marketing/"
          id="header-landlords-marketing"
        >
          {t('Marketing', 'Marketing')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/leases/"
          id="header-landlords-lease"
        >
          {t('Leases', 'Leases')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/automation/"
          id="header-landlords-automation"
        >
          {t('Automation', 'Automation')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/manage/"
          id="header-landlords-management"
        >
          {t('Management', 'Management')}
        </SidebarLinkMobile>
        <Link href="/landlords" passHref legacyBehavior>
          <SeeAllFeaturesLink id="header-landlords-see-all-features-btn">
            {t('See all features', 'See all features')}
            <ArrowRightIconBlue width={12} height={12} />
          </SeeAllFeaturesLink>
        </Link>
        <Divider />
        <SidebarLabelMobile>{t('Use case', 'Use case')}</SidebarLabelMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/landlords/"
          id="header-landlords-usecase-landlords"
        >
          {t('Landlords', 'Landlords')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/propertymanagers/"
          id="header-landlords-usecase-pms"
        >
          {t('Property Managers', 'Property Managers')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://landlords.liv.rent/brokerages/"
          id="header-landlords-usecase-brokerages"
        >
          {t('Brokerages', 'Brokerages')}
        </SidebarLinkMobile>
      </MenuOptions>
    )
  }

  const renderRentersSubMenuMobile = t => {
    return (
      <MenuOptions>
        <SidebarLabelMobile>{t('Features', 'Features')}</SidebarLabelMobile>
        <SidebarLinkMobile
          target="_self"
          href="/rental-listings"
          id="header-renters-browse-listings"
        >
          {t('Browse Listings', 'Browse Listings')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://renters.liv.rent/verified/"
          id="header-renters-verified-listings"
        >
          {t('Verified Listings', 'Verified Listings')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://renters.liv.rent/automation/"
          id="header-renters-automation"
        >
          {t('Automation', 'Automation')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://renters.liv.rent/bitcoin/"
          id="header-renters-bitcoin-payment"
        >
          {t('Bitcoin Payments', 'Bitcoin Payments')}
        </SidebarLinkMobile>
        <Link href="/renters" passHref legacyBehavior>
          <SeeAllFeaturesLink id="header-renters-see-all-features-btn">
            {t('See all features', 'See all features')}
            <ArrowRightIconBlue width={12} height={12} />
          </SeeAllFeaturesLink>
        </Link>
      </MenuOptions>
    )
  }

  const renderResourcesSubMenuMobile = t => {
    return (
      <MenuOptions>
        <SidebarLabelMobile>
          {t('Rent Reports', 'Rent Reports')}
        </SidebarLabelMobile>
        <SidebarLinkMobile
          href="https://liv.rent/blog/rent-reports/vancouver/"
          id="header-resources-van-rentreport"
        >
          {t('Vancouver', 'Vancouver')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://liv.rent/blog/rent-reports/toronto/"
          id="header-resources-to-rentreport"
        >
          {t('Toronto', 'Toronto')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://liv.rent/blog/rent-reports/montreal/"
          id="header-resources-mtl-rentreport"
        >
          {t('Montreal', 'Montreal')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://liv.rent/blog/rent-reports/alberta/"
          id="header-resources-ab-rentreport"
        >
          {t('Alberta', 'Alberta')}
        </SidebarLinkMobile>
        <Divider />
        <SidebarLabelMobile>{t('Learn', 'Learn')}</SidebarLabelMobile>
        <SidebarLinkMobile
          href="/about"
          target="_self"
          id="header-resources-about"
        >
          {t('About', 'About')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://liv.rent/blog/renters/"
          id="header-resources-renter-resources"
        >
          {t('Renter Resources', 'Renter Resources')}
        </SidebarLinkMobile>
        <SidebarLinkMobile
          href="https://liv.rent/blog/landlords/"
          id="header-resources-landlord-resources"
        >
          {t('Landlord Resources', 'Landlord Resources')}
        </SidebarLinkMobile>
        <SidebarLinkMobile href="/faq" target="_self" id="header-resources-faq">
          {t('FAQ', 'FAQ')}
        </SidebarLinkMobile>
      </MenuOptions>
    )
  }

  const getHeaderOption = () => {
    switch (menuType) {
      case 'Manage':
        return renderManageSidebarHeader(t, clickedBack, closeSidebar)
      case 'Landlords':
        return renderLandlordsSidebarHeader(t, clickedBack, closeSidebar)
      case 'Renters':
        return renderRentersSidebarHeader(t, clickedBack, closeSidebar)
      case 'Resources':
        return renderResourcesSidebarHeader(t, clickedBack, closeSidebar)
      default:
        return (
          <SidebarHeader hasLogo>
            <LeftSideOfHeader t={t} />
            <CloseButton onClick={closeSidebar}>
              <CloseButtonGrey96 width={32} height={32} />
            </CloseButton>
          </SidebarHeader>
        )
    }
  }

  const getMenuOption = () => {
    switch (menuType) {
      case 'Manage':
        return renderManageSubMenuMobile(t)
      case 'Landlords':
        return renderLandlordsSubMenuMobile(t)
      case 'Renters':
        return renderRentersSubMenuMobile(t)
      case 'Resources':
        return renderResourcesSubMenuMobile(t)
      default:
        if (hasSession)
          return isTenant
            ? renderTenantMenuMobile()
            : renderLandlordMenuMobile()
        return renderPublicMenuMobile()
    }
  }

  return (
    <SidebarContainer
      isOpen={isOpen}
      ref={sidebarRef}
      initial={{ left: '100%' }}
      animate={{ left: 0 }}
      transition={{
        duration: 0.45
      }}
    >
      <NoScroll />
      {getHeaderOption()}
      {getMenuOption()}
    </SidebarContainer>
  )
}

export default Sidebar
